import React, { useCallback, useMemo, useState } from "react";
import { Checkbox, FormControlLabel } from "@mui/material";
import { Popconfirm, Tooltip } from "antd";
import { useRecoilValue, useSetRecoilState } from "recoil";
import SmartIcon from "../../../../components/generic/genericIcon/SmartIcon";
import PrimaryButton from "../../../../components/generic/buttons/PrimaryButton";
import UserAvatar from "../UserAvatar/UserAvatar";
import AddGroupMembersModal from "./AddGroupMembersModal";
import EditableGroupDetails from "./EditableGroupDetails";
import { getFullName } from "../../../careprovider/visits/utils";
import { chatRoomsState, selectedChatRoomState } from "../../state/chatState";
import {
  canRemoveGroupMember,
  getActiveRoomMembers,
  getChatRoomId,
  isGroupChatAdmin,
  normalizeWorkforceToRoomMember,
} from "../../chatRoomUtils";
import apiService from "../../../../service/apiservice/apiService";
import { useReadMemory } from "../../../generic/hooks/useReadMemory";
import MemoryType from "../../../../service/memory/enum/MemoryType";
import "./GroupChatDetailsPanel.css";
import { useNavigate } from "react-router";
import { RoleToLinkMapper } from "../../../utils/UrlUtils";

const GroupChatDetailsPanel = ({ currentUserIdentifier }) => {
  const userJson = useReadMemory(MemoryType.USER);
  const navigate = useNavigate();
  const room = useRecoilValue(selectedChatRoomState);
  const setChatRooms = useSetRecoilState(chatRoomsState);
  const [isAddMembersModalOpen, setIsAddMembersModalOpen] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState(null);
  const [adminRoleUpdatingMemberId, setAdminRoleUpdatingMemberId] = useState(null);
  const [adminOnlyUpdating, setAdminOnlyUpdating] = useState(false);

  const isAdmin = isGroupChatAdmin(room, currentUserIdentifier);
  const isCurrentUserRoomOwner =
    String(room?.createdBy) === String(currentUserIdentifier);
  const roomId = getChatRoomId(room);

  const members = useMemo(() => {
    const list = room?.members || [];
    const currentId = String(currentUserIdentifier ?? "");

    return [...list].sort((left, right) => {
      const leftIsCurrent = String(left?.userIdentifier) === currentId;
      const rightIsCurrent = String(right?.userIdentifier) === currentId;

      if (leftIsCurrent !== rightIsCurrent) {
        return leftIsCurrent ? -1 : 1;
      }

      const leftName = getFullName(left?.givenName, left?.familyName) || "";
      const rightName = getFullName(right?.givenName, right?.familyName) || "";
      return leftName.localeCompare(rightName);
    });
  }, [currentUserIdentifier, room?.members]);

  const activeMembers = useMemo(() => getActiveRoomMembers(members), [members]);

  const existingMemberIds = useMemo(
    () => activeMembers.map((member) => member?.userIdentifier).filter(Boolean),
    [activeMembers],
  );

  const handleProfileView = (member) => {
    navigate(
      `${RoleToLinkMapper(userJson?.jobTitle)}volunteer-profile?identifier=${member?.userIdentifier}`,
    );
    window.scrollTo(0, 0);
  };

  const applyLocalRoomUpdate = useCallback(
    (updates) => {
      setChatRooms((prevRooms) =>
        prevRooms.map((chatRoom) =>
          getChatRoomId(chatRoom) === roomId
            ? { ...chatRoom, ...updates }
            : chatRoom,
        ),
      );
    },
    [roomId, setChatRooms],
  );

  const updateRoomDetails = useCallback(
    async (updates) => {
      if (!room?.roomId) {
        return false;
      }

      const mergedRoom = { ...room, ...updates };
      const payload = {
        roomId: Number(room.roomId),
        organizationIdentifier: userJson?.organizationIdentifier,
        title: mergedRoom.title ?? null,
        topic: mergedRoom.topic ?? null,
        updatedBy: userJson?.userIdentifier,
        ...(Object.hasOwn(updates, "adminOnly")
          ? { adminOnly: Boolean(mergedRoom.adminOnly) }
          : mergedRoom.adminOnly
            ? { adminOnly: true }
            : {}),
      };

      const result = await apiService.post(
        "niramayafoundation/api/v1/communication/room",
        payload,
        "Chat room updated",
        "Could not update group details",
      );

      if (!result) {
        return false;
      }

      applyLocalRoomUpdate(updates);
      return true;
    },
    [
      applyLocalRoomUpdate,
      room,
      userJson?.organizationIdentifier,
      userJson?.userIdentifier,
    ],
  );

  const handleMembersAdded = useCallback(
    (addedUsers, apiResult) => {
      if (Array.isArray(apiResult?.members)) {
        applyLocalRoomUpdate({ members: apiResult.members });
        return;
      }

      const existingIds = new Set(
        (room?.members || []).map((member) => String(member?.userIdentifier)),
      );
      const newMembers = addedUsers
        .map(normalizeWorkforceToRoomMember)
        .filter(
          (member) =>
            member.userIdentifier &&
            !existingIds.has(String(member.userIdentifier)),
        );

      if (!newMembers.length) {
        return;
      }

      applyLocalRoomUpdate({
        members: [...(room?.members || []), ...newMembers],
      });
    },
    [applyLocalRoomUpdate, room?.members],
  );

  const handleRemoveMember = useCallback(
    async (member) => {
      if (
        !room?.roomId ||
        !member?.userIdentifier ||
        !canRemoveGroupMember(room, member, activeMembers.length) ||
        removingMemberId
      ) {
        return;
      }

      const memberId = member.userIdentifier;
      const payload = {
        roomId: Number(room.roomId),
        organizationIdentifier: userJson?.organizationIdentifier,
        updatedBy: userJson?.userIdentifier,
        removeMemberUserIdentifiers: [memberId],
      };

      setRemovingMemberId(memberId);
      try {
        const result = await apiService.post(
          "niramayafoundation/api/v1/communication/room",
          payload,
          "Member removed",
          "Could not remove member from the group",
        );

        if (!result) {
          return;
        }

        if (Array.isArray(result?.members)) {
          applyLocalRoomUpdate({ members: result.members });
          return;
        }

        applyLocalRoomUpdate({
          members: members.map((existingMember) =>
            String(existingMember.userIdentifier) === String(memberId)
              ? { ...existingMember, activeInRoom: false }
              : existingMember,
          ),
        });
      } finally {
        setRemovingMemberId(null);
      }
    },
    [
      activeMembers,
      applyLocalRoomUpdate,
      members,
      removingMemberId,
      room,
      userJson?.organizationIdentifier,
      userJson?.userIdentifier,
    ],
  );

  const updateMemberAdminRole = useCallback(
    async (member, action) => {
      const memberId = member?.userIdentifier;
      const isRoomOwner = String(memberId) === String(room?.createdBy);
      const isMemberAdmin =
        isRoomOwner || Boolean(member?.isAdmin ?? member?.admin);

      if (
        !isCurrentUserRoomOwner ||
        !room?.roomId ||
        !memberId ||
        isRoomOwner ||
        adminRoleUpdatingMemberId ||
        (action === "make" && isMemberAdmin) ||
        (action === "remove" && !isMemberAdmin)
      ) {
        return;
      }

      const isMakeAdmin = action === "make";
      const payload = {
        roomId: Number(room.roomId),
        organizationIdentifier: userJson?.organizationIdentifier,
        updatedBy: userJson?.userIdentifier,
        [isMakeAdmin ? "makeAdmin" : "removeAdmin"]: [memberId],
      };

      setAdminRoleUpdatingMemberId(memberId);
      try {
        const result = await apiService.post(
          "niramayafoundation/api/v1/communication/room",
          payload,
          isMakeAdmin ? "Member promoted to admin" : "Admin role removed",
          isMakeAdmin
            ? "Could not promote member to admin"
            : "Could not remove admin role",
        );

        if (!result) {
          return;
        }

        if (Array.isArray(result?.members)) {
          applyLocalRoomUpdate({ members: result.members });
          return;
        }

        applyLocalRoomUpdate({
          members: members.map((existingMember) =>
            String(existingMember.userIdentifier) === String(memberId)
              ? {
                  ...existingMember,
                  isAdmin: isMakeAdmin,
                  admin: isMakeAdmin,
                }
              : existingMember,
          ),
        });
      } finally {
        setAdminRoleUpdatingMemberId(null);
      }
    },
    [
      adminRoleUpdatingMemberId,
      applyLocalRoomUpdate,
      isCurrentUserRoomOwner,
      members,
      room,
      userJson?.organizationIdentifier,
      userJson?.userIdentifier,
    ],
  );

  const handleMakeAdmin = useCallback(
    (member) => updateMemberAdminRole(member, "make"),
    [updateMemberAdminRole],
  );

  const handleRemoveAdmin = useCallback(
    (member) => updateMemberAdminRole(member, "remove"),
    [updateMemberAdminRole],
  );

  const handleAdminOnlyChange = useCallback(
    async (event) => {
      if (!isAdmin || adminOnlyUpdating) {
        return;
      }

      const nextAdminOnly = event.target.checked;
      setAdminOnlyUpdating(true);
      try {
        await updateRoomDetails({ adminOnly: nextAdminOnly });
      } finally {
        setAdminOnlyUpdating(false);
      }
    },
    [adminOnlyUpdating, isAdmin, updateRoomDetails],
  );

  return (
    <>
      <section className="group-chat-details-panel" aria-label="Group details">
        <div className="group-chat-details-panel-content">
          <div className="group-chat-details-meta-card">
            <EditableGroupDetails
              title={room?.title ?? ""}
              topic={room?.topic ?? ""}
              isAdmin={isAdmin}
              onSave={updateRoomDetails}
            />
            {isAdmin ? (
              <FormControlLabel
                className="group-chat-details-admin-only"
                control={
                  <Checkbox
                    name="adminOnly"
                    checked={Boolean(room?.adminOnly)}
                    onChange={handleAdminOnlyChange}
                    disabled={adminOnlyUpdating}
                  />
                }
                label="Only admins can send messages"
              />
            ) : null}
          </div>

          <div className="group-chat-details-members">
            <div className="group-chat-details-members-header">
              <h4 className="group-chat-details-members-title">
                Members ({activeMembers.length})
              </h4>
              {isAdmin ? (
                <PrimaryButton
                  buttonText="Add Members"
                  handleOnClick={() => setIsAddMembersModalOpen(true)}
                />
              ) : null}
            </div>
            <ul className="group-chat-details-members-list chat-scroll-y">
              {activeMembers.map((member) => {
                const displayName =
                  getFullName(member?.givenName, member?.familyName) ||
                  "Unknown";
                const isCurrentUser =
                  String(member?.userIdentifier) ===
                  String(currentUserIdentifier);
                const isMemberRoomOwner =
                  String(member?.userIdentifier) === String(room?.createdBy);
                const isMemberAdmin =
                  isMemberRoomOwner || Boolean(member?.isAdmin ?? member?.admin);
                const canManageAdminRole =
                  !isCurrentUser && isCurrentUserRoomOwner && !isMemberRoomOwner;
                const canRemove =
                  isAdmin && !isCurrentUser &&
                  canRemoveGroupMember(room, member, activeMembers.length);
                const isRemovingThisMember =
                  String(removingMemberId) === String(member?.userIdentifier);
                const isUpdatingAdminRole =
                  String(adminRoleUpdatingMemberId) ===
                  String(member?.userIdentifier);

                return (
                  <li
                    key={member?.userIdentifier}
                    className="group-chat-details-member"
                  >
                    <UserAvatar
                      user={{
                        givenName: member?.givenName,
                        familyName: member?.familyName,
                        name: displayName,
                      }}
                      size={36}
                    />
                    <div className="group-chat-details-member-info">
                      <span className="group-chat-details-member-name">
                        {displayName}
                        {isCurrentUser ? " (You)" : ""}
                        {isMemberAdmin ? " · Admin" : ""}
                      </span>
                      {member?.jobTitle ? (
                        <span className="group-chat-details-member-role">
                          {member.jobTitle}
                        </span>
                      ) : null}
                    </div>
                    {canManageAdminRole && !isMemberAdmin ? (
                      <Tooltip title="Make Admin" placement="left">
                        <span
                          className={`group-chat-details-member-action ${
                            isUpdatingAdminRole ? "is-removing" : ""
                          }`}
                          role="button"
                          tabIndex={adminRoleUpdatingMemberId ? -1 : 0}
                          aria-label={`Make ${displayName} an admin`}
                          aria-disabled={Boolean(adminRoleUpdatingMemberId)}
                        >
                          <SmartIcon
                            iconKey="makeAdmin"
                            width={18}
                            height={18}
                            handleIconClick={() => handleMakeAdmin(member)}
                          />
                        </span>
                      </Tooltip>
                    ) : null}
                    {canManageAdminRole && isMemberAdmin ? (
                      <Tooltip title="Remove Admin" placement="left">
                        <span
                          className={`group-chat-details-member-action ${
                            isUpdatingAdminRole ? "is-removing" : ""
                          }`}
                          role="button"
                          tabIndex={adminRoleUpdatingMemberId ? -1 : 0}
                          aria-label={`Remove admin role from ${displayName}`}
                          aria-disabled={Boolean(adminRoleUpdatingMemberId)}
                        >
                          <SmartIcon
                            iconKey="removeAdmin"
                            width={18}
                            height={18}
                            handleIconClick={() => handleRemoveAdmin(member)}
                          />
                        </span>
                      </Tooltip>
                    ) : null}
                    {!isCurrentUser &&
                      userJson?.jobTitle === "Volunteer Coordinator" && (
                        <Tooltip title="View Profile" placement="left">
                          <>
                            <SmartIcon
                              iconKey="profile"
                              width={18}
                              height={18}
                              handleIconClick={() => handleProfileView(member)}
                            />
                          </>
                        </Tooltip>
                      )}
                    {canRemove ? (
                      <Popconfirm
                        title={`Remove ${displayName} from this group?`}
                        placement="topLeft"
                        okText="Remove"
                        cancelText="Cancel"
                        disabled={Boolean(
                          removingMemberId || adminRoleUpdatingMemberId,
                        )}
                        onConfirm={() => handleRemoveMember(member)}
                      >
                        <Tooltip title="Remove member" placement="left">
                          <span
                            className={`group-chat-details-member-action ${
                              isRemovingThisMember ? "is-removing" : ""
                            }`}
                            role="button"
                            tabIndex={0}
                            aria-label={`Remove ${displayName}`}
                          >
                            <SmartIcon
                              iconKey="delete"
                              width={18}
                              height={18}
                            />
                          </span>
                        </Tooltip>
                      </Popconfirm>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {isAddMembersModalOpen ? (
        <AddGroupMembersModal
          isOpen={isAddMembersModalOpen}
          onClose={() => setIsAddMembersModalOpen(false)}
          roomId={room?.roomId}
          existingMemberIds={existingMemberIds}
          onMembersAdded={handleMembersAdded}
        />
      ) : null}
    </>
  );
};

export default GroupChatDetailsPanel;
